import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lightbulb, CheckCircle2, AlertTriangle, XCircle, Target, ChevronDown, Skull, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface PreviousContext {
  hypothesis?: string;
  successSignal?: string;
  conflictStrategyA?: string;
  conflictStrategyB?: string;
}

interface Phase14KillerAssumptionInputProps {
  onValueChange: (value: string) => void;
  onStructuredChange?: (data: { killerAssumption: string }) => void;
  previousContext?: PreviousContext;
  className?: string;
}

type AssumptionQuality = 'none' | 'weak' | 'medium' | 'strong';

const MAX_CHARS = 180;
const CHAR_WARNING_THRESHOLD = 160;

export const Phase14KillerAssumptionInput = ({
  onValueChange,
  onStructuredChange,
  previousContext,
  className
}: Phase14KillerAssumptionInputProps) => {
  const [assumption, setAssumption] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    onValueChange(assumption);
    if (assumption.trim()) {
      onStructuredChange?.({ killerAssumption: assumption.trim() });
    }
  }, [assumption, onValueChange, onStructuredChange]);

  // Assumption quality check - looking for falsifiability and specificity
  const assumptionAnalysis = useMemo(() => {
    const trimmed = assumption.trim().toLowerCase();
    
    if (trimmed.length < 15) {
      return { quality: 'none' as AssumptionQuality, tip: null };
    }

    // Check for vague terms
    const vagueTerms = ['vielleicht', 'eventuell', 'irgendwie', 'möglicherweise', 'könnte', 'würde'];
    const hasVague = vagueTerms.some(term => trimmed.includes(term));

    // Check for conditional/falsifiable structure
    const conditionalTerms = ['wenn', 'falls', 'sofern', 'nicht', 'kein', 'nie', 'falsch', 'scheitert', 'kippt', 'hält nicht'];
    const hasConditional = conditionalTerms.some(term => trimmed.includes(term));

    // Check for killer assumption indicators
    const killerTerms = ['kritisch', 'entscheidend', 'fundamental', 'kern', 'basis', 'voraussetzung', 'muss', 'zwingend', 'ohne'];
    const hasKillerIndicator = killerTerms.some(term => trimmed.includes(term));

    // Check for concrete subject
    const concreteTerms = ['kunden', 'nutzer', 'markt', 'team', 'produkt', 'umsatz', 'nachfrage', 'bedarf', 'preis', 'zeit', 'budget', 'ressource'];
    const hasConcrete = concreteTerms.some(term => trimmed.includes(term));

    // Determine quality
    if (hasVague && !hasConditional) {
      return { 
        quality: 'weak' as AssumptionQuality, 
        tip: 'Zu vage – welche spezifische Annahme könnte kippen?'
      };
    }
    if (!hasConditional && trimmed.length < 40) {
      return { 
        quality: 'weak' as AssumptionQuality, 
        tip: 'Formuliere: "Wenn X nicht zutrifft, dann scheitert Y."'
      };
    }
    if ((hasConditional || hasKillerIndicator) && hasConcrete) {
      return { quality: 'strong' as AssumptionQuality, tip: null };
    }
    if (hasConditional || hasKillerIndicator) {
      return { 
        quality: 'medium' as AssumptionQuality, 
        tip: 'Fast da – woran würdest du erkennen, dass sie falsch ist?'
      };
    }
    if (hasConcrete) {
      return { 
        quality: 'medium' as AssumptionQuality, 
        tip: 'Konkret, aber: Was genau müsste falsch sein?'
      };
    }
    
    return { 
      quality: 'medium' as AssumptionQuality, 
      tip: 'Schärfe: Welche Annahme würde alles kippen?'
    };
  }, [assumption]);

  const hasContent = assumption.trim().length > 15;

  // Show context from previous hypothesis if available
  const hasContext = previousContext?.hypothesis || previousContext?.successSignal;

  return (
    <div className={cn("space-y-5", className)}>
      {/* Scharfe Führung - maximale Klarheit */}
      <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-destructive/10">
            <Skull className="w-5 h-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Killer-Annahme identifizieren
            </h3>
            <p className="text-[15px] text-foreground/80 leading-relaxed">
              Vervollständige: <span className="font-medium text-foreground">„Wenn X nicht stimmt, ist alles andere irrelevant."</span>
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Die eine Annahme, die du zuerst validieren musst – bevor du weitermachst.
            </p>
          </div>
        </div>
      </div>

      {/* Context from previous steps */}
      {hasContext && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground bg-muted/30 rounded-lg py-3 px-4 border border-border/30">
          <Target className="w-4 h-4 text-primary flex-shrink-0" />
          <span>
            <span className="text-muted-foreground">Kernziel:</span>{' '}
            <span className="font-medium text-foreground">{previousContext?.successSignal || previousContext?.hypothesis}</span>
          </span>
        </div>
      )}

      {/* Main killer assumption card */}
      <div className={cn(
        "p-5 rounded-xl border-2 transition-all",
        isFocused 
          ? "border-destructive bg-destructive/5 shadow-lg shadow-destructive/10" 
          : "border-border bg-card/50"
      )}>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-destructive/15 flex items-center justify-center">
              <Skull className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <Label className="text-base font-semibold text-foreground block">
                Deine Killer-Annahme
              </Label>
              <span className="text-sm text-muted-foreground">
                Was darf auf keinen Fall falsch sein?
              </span>
            </div>
          </div>
          
          {/* Single input field */}
          <div className="relative">
            <Input
              value={assumption}
              onChange={(e) => setAssumption(e.target.value.slice(0, MAX_CHARS))}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="z.B. Ohne ausreichende Nachfrage im Segment X ist der gesamte Plan wertlos"
              className={cn(
                "pr-14 text-base bg-background border-2 h-12",
                "placeholder:text-muted-foreground/50",
                isFocused ? "border-destructive" : "border-border/50"
              )}
            />
            {assumption.length > CHAR_WARNING_THRESHOLD && (
              <span className={cn(
                "absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium",
                assumption.length >= MAX_CHARS ? "text-destructive" : "text-muted-foreground"
              )}>
                {assumption.length}/{MAX_CHARS}
              </span>
            )}
          </div>

          {/* Micro-nudge - schärfer */}
          <p className="text-xs text-muted-foreground text-center">
            Wenn du in 6 Monaten sagst „Das hätten wir wissen müssen" – was wäre es?
          </p>
        </div>
      </div>

      {/* Quality indicator */}
      {hasContent && (
        <div className={cn(
          "p-4 rounded-xl border-2 transition-all animate-in fade-in slide-in-from-bottom-2 duration-200",
          assumptionAnalysis.quality === 'strong' && "bg-emerald-500/10 border-emerald-500/40",
          assumptionAnalysis.quality === 'medium' && "bg-amber-500/10 border-amber-500/40",
          assumptionAnalysis.quality === 'weak' && "bg-destructive/10 border-destructive/40",
        )}>
          <div className="flex items-start gap-3">
            {assumptionAnalysis.quality === 'strong' && (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            )}
            {assumptionAnalysis.quality === 'medium' && (
              <Lightbulb className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            )}
            {assumptionAnalysis.quality === 'weak' && (
              <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <p className={cn(
                "text-base font-semibold",
                assumptionAnalysis.quality === 'strong' && "text-emerald-700 dark:text-emerald-300",
                assumptionAnalysis.quality === 'medium' && "text-amber-700 dark:text-amber-300",
                assumptionAnalysis.quality === 'weak' && "text-destructive",
              )}>
                {assumptionAnalysis.quality === 'strong' && "✓ Klare Killer-Annahme"}
                {assumptionAnalysis.quality === 'medium' && "Fast klar – noch präzisieren"}
                {assumptionAnalysis.quality === 'weak' && "Noch zu unscharf"}
              </p>
              {assumptionAnalysis.tip && (
                <p className="text-sm text-muted-foreground mt-1">
                  {assumptionAnalysis.tip}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Thinking aid - collapsed by default */}
      <Collapsible>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group mx-auto py-2 px-4 rounded-lg hover:bg-muted/30">
          <HelpCircle className="w-4 h-4" />
          <span className="font-medium">Was ist eine Killer-Annahme?</span>
          <ChevronDown className="w-4 h-4 transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 animate-in slide-in-from-top-2 duration-200">
          <div className="p-5 bg-muted/20 border border-border/40 rounded-xl space-y-4">
            <div>
              <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                Die richtige Frage
              </p>
              <p className="text-sm text-muted-foreground bg-background/50 p-3 rounded-lg border border-border/30 italic">
                „Wenn ich in 6 Monaten sage: <span className="text-foreground font-medium">Das hätten wir vorher wissen müssen</span> – was wäre es?"
              </p>
            </div>
            
            <div className="pt-3 border-t border-border/30">
              <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Skull className="w-4 h-4 text-destructive" />
                Typische Killer-Annahmen
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-destructive font-bold">•</span>
                  <span>Nachfrage existiert in ausreichender Größe</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive font-bold">•</span>
                  <span>Team kann technisch liefern</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive font-bold">•</span>
                  <span>Regulierung erlaubt es</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive font-bold">•</span>
                  <span>Finanzierung hält mindestens bis X</span>
                </li>
              </ul>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
